import dayjs from 'dayjs'
import { Injectable } from '@tanbo/vue-di-plugin'
import { Http } from '@/services/http'
import { getTimeRangeByEnum } from '@/utils/time'
import { TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'

/** 安全态势统计区间（秒级时间戳字符串） */
export interface SafeStatusRequestParams {
  start_time: string
  end_time: string
}

function buildSafeStatusBody (params: {
  time_range?: string
  start_time?: string
  end_time?: string
}): SafeStatusRequestParams {
  const hasExplicit =
    params.start_time != null &&
    params.end_time != null &&
    params.start_time !== '' &&
    params.end_time !== ''
  if (hasExplicit) {
    return {
      start_time: String(params.start_time),
      end_time: String(params.end_time),
    }
  }
  const tr = params.time_range || TimeRange.DAYS_30
  const [start, end] = getTimeRangeByEnum(tr as TimeRange)
  const endSec = end ?? Math.floor(Date.now() / 1000)
  const startSec =
    start ?? Math.floor(dayjs().subtract(365, 'day').valueOf() / 1000)
  return {
    start_time: String(startSec),
    end_time: String(endSec),
  }
}

/** 安全评估等级 */
export enum SecurityLevel {
  /** 优秀 */
  EXCELLENT = 1,
  /** 良好 */
  GOOD = 2,
  /** 危险 */
  DANGER = 3
}

/** 安全态势接口响应 */
export interface SafeStatusData {
   /** 最终结果，1 极好，2 良好，3 不好 */
   final_results: number
   /** 团队分数 */
   score: number
   /** 低分主机数（体检60分以下） */
   physical_substandard_count: number
   /** 体检60-99分主机数 */
   physical_standard_count: number
   /** 体检100分主机数 */
   physical_perfect_count: number
   /** 【新增】未体检终端数 */
   not_examined_count: number
   /** 感染主机数（病毒影响终端数） */
   virus_affect_count: number
   /** 未处置病毒数（只统计未处理的，失败的不算） */
   not_dealt_virus_count: number
   /** 高危漏洞影响主机数 */
   leak_affect_count: number
   /** 未处理高危漏洞数 */
   not_dealt_leak_count: number
   /** 基线违规主机数 */
   baseline_substandard_count: number
   /** 【新增】基线不合规项数（去重统计） */
   baseline_non_compliance_total: number
   /** 风险端口影响主机数 */
   port_affect_count: number
   /** 开放危险端口数 */
   danger_port_count: number
   /** 【新增】勒索风险高危主机数 */
   ransomware_affect_count: number
   /** 【新增】勒索高危事件数（仅统计高危等级 level=4） */
   ransomware_event_count: number
   /** 【新增】隔离终端数 */
   isolation_count: number
   /** 上周勒索高危事件数（与 ransomware_event_count 对应，用于同比） */
   last_ransomware_event_count?: number
   /** 上周低分主机数（体检60分以下） */
   last_physical_substandard_count: number
   /** 上周勒索风险高危主机数 */
   last_ransomware_affect_count: number
   /** 上周未处理病毒木马 */
   last_not_dealt_virus_count: number
   /** 上周病毒影响终端数 */
   last_virus_affect_count: number
   /** 上周未处理高危漏洞 */
   last_not_dealt_leak_count: number
   /** 上周漏洞影响终端数 */
   last_leak_affect_count: number
   /** 上周基线扫描不合规 */
   last_baseline_substandard_count: number
   /** 上周开放危险端口数 */
   last_danger_port_count: number
   /** 上周端口影响终端数 */
   last_port_affect_count: number
}

/** 安全态势指标卡片配置（文案键见 overviewZone.*） */
export interface IndicatorConfig {
  key: string
  labelKey: string
  valueField: keyof SafeStatusData
  detailLabelKey: string
  detailField: keyof SafeStatusData
  detailUnitKey: string
  actionLabelKey: string
  route: string
}

/**
 * 指标主数值字段 → 接口「上周」同口径字段，用于计算同比上周（本周值 − 上周值）。
 */
export const INDICATOR_WOW_LAST_FIELD: Partial<
  Record<keyof SafeStatusData, keyof SafeStatusData>
> = {
  physical_substandard_count: 'last_physical_substandard_count',
  virus_affect_count: 'last_virus_affect_count',
  leak_affect_count: 'last_leak_affect_count',
  baseline_substandard_count: 'last_baseline_substandard_count',
  port_affect_count: 'last_port_affect_count',
  ransomware_affect_count: 'last_ransomware_affect_count',
}

/** 指标卡片列表（按设计图顺序：全面体检、病毒查杀、漏洞修复、基线合规、端口管控、勒索风险） */
export const INDICATOR_LIST: IndicatorConfig[] = [
  {
    key: 'low_score',
    labelKey: 'overviewZone.indLowScore_label',
    valueField: 'physical_substandard_count',
    detailLabelKey: 'overviewZone.indLowScore_detail',
    detailField: 'not_examined_count',
    detailUnitKey: 'overviewZone.unitTai',
    actionLabelKey: 'overviewZone.actionExamination',
    route: '/dashboard/examination-status'
  },
  {
    key: 'infected',
    labelKey: 'overviewZone.indInfected_label',
    valueField: 'virus_affect_count',
    detailLabelKey: 'overviewZone.indInfected_detail',
    detailField: 'not_dealt_virus_count',
    detailUnitKey: 'overviewZone.unitPiece',
    actionLabelKey: 'overviewZone.actionVirusScan',
    route: '/dashboard/loaris-trojan-remover'
  },
  {
    key: 'vulnerability',
    labelKey: 'overviewZone.indVuln_label',
    valueField: 'leak_affect_count',
    detailLabelKey: 'overviewZone.indVuln_detail',
    detailField: 'not_dealt_leak_count',
    detailUnitKey: 'overviewZone.unitPiece',
    actionLabelKey: 'overviewZone.actionVulnFix',
    route: '/dashboard/vulnerability-fix'
  },
  {
    key: 'violation',
    labelKey: 'overviewZone.indViolation_label',
    valueField: 'baseline_substandard_count',
    detailLabelKey: 'overviewZone.indViolation_detail',
    detailField: 'baseline_non_compliance_total',
    detailUnitKey: 'overviewZone.unitXiang',
    actionLabelKey: 'overviewZone.actionBaseline',
    route: '/dashboard/base-line'
  },
  {
    key: 'risk_port',
    labelKey: 'overviewZone.indRiskPort_label',
    valueField: 'port_affect_count',
    detailLabelKey: 'overviewZone.indRiskPort_detail',
    detailField: 'danger_port_count',
    detailUnitKey: 'overviewZone.unitPiece',
    actionLabelKey: 'overviewZone.actionPortControl',
    route: '/dashboard/port-manage'
  },
  {
    key: 'ransomware',
    labelKey: 'overviewZone.indRansomware_label',
    valueField: 'ransomware_affect_count',
    detailLabelKey: 'overviewZone.indRansomware_detail',
    detailField: 'ransomware_event_count',
    detailUnitKey: 'overviewZone.unitPiece',
    actionLabelKey: 'overviewZone.actionRansomware',
    route: '/dashboard/ransomware-incident'
  }
]

@Injectable()
export class SecurityStatusService {
  constructor (private http: Http) {}

  /** 获取安全态势数据（请求体含 start_time / end_time） */
  getSecurityStatus (params?: {
    time_range?: string
    start_time?: string
    end_time?: string
  }) {
    return this.http.post<SafeStatusData>(
      '/homepage/safe-status',
      buildSafeStatusBody(params || {}),
    )
  }
}
